import {
    AfterViewInit,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import { ImportStatusType, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { tap } from 'rxjs/operators';
import { IMPORT_FORM_MODAL } from './shared/constants';

import { ModalManagementService } from '@wdx/clmi/ui';
import { WdxToastService } from '@wdx/shared/components/wdx-toast';
import {
    ROUTE_PREFIX,
    Severity,
    TRANSLATION_TOAST_MESSAGE_BACKGROUND_JOB_GENERATED,
    TRANSLATION_TOAST_MESSAGE_IMPORT_FILE_UNSUCCESSFUL,
    TranslationsService,
    URL_DATA_MANAGEMENT_IMPORT,
} from '@wdx/shared/utils';
import { ImportTypeTypecodeApiService } from '@wdx/clmi/clmi-swagger-gen/api-services';
import {
    DynamicPagesEventTypes,
    DynamicPagesFacade,
} from '../../../features/dynamic-pages/+state';

@Component({
    selector: 'clmi-import-file-uploader',
    templateUrl: './import-file-uploader.component.html',
    standalone: false,
})
export class ImportFileUploaderComponent implements AfterViewInit, OnDestroy {
    private importApiService = inject(ImportTypeTypecodeApiService);
    private modalManagementService = inject(ModalManagementService);
    private toastService = inject(WdxToastService);
    private dynamicPagesFacade = inject(DynamicPagesFacade);
    @Input() typeCode: string;

    files: File[] = [];
    isLoadingData = false;

    readonly IMPORT_FORM_MODAL = IMPORT_FORM_MODAL;

    @Output() clmiAfterViewInit = new EventEmitter();
    @Output() clmiOnClosed = new EventEmitter();

    private translationsService = inject(TranslationsService);

    ngAfterViewInit(): void {
        this.clmiAfterViewInit.emit();
    }

    ngOnDestroy(): void {
        this.clearFiles();
    }

    onClose(): void {
        this.clearFiles();
        this.closeModal();
    }

    onSelect(event) {
        this.files.push(...event.addedFiles);
    }

    onRemove(event) {
        this.files.splice(this.files.indexOf(event), 1);
    }

    onClick(): void {
        const formData = this.getImportFormData(this.files[0]);

        this.importApiService
            .post(this.typeCode, formData)
            .pipe(tap(() => (this.isLoadingData = true)))

            .subscribe(
                (_) => undefined,
                () => {
                    this.clearFileUpload(
                        Severity.Danger,
                        this.translationsService.getTranslationByKey(
                            TRANSLATION_TOAST_MESSAGE_IMPORT_FILE_UNSUCCESSFUL,
                        ),
                    );
                },
                () => {
                    this.clearFileUpload(
                        Severity.Success,
                        this.translationsService.getTranslationByKey(
                            TRANSLATION_TOAST_MESSAGE_BACKGROUND_JOB_GENERATED,
                        ),
                    );
                },
            );
    }

    clearFileUpload(severity: Severity, message: string): void {
        this.isLoadingData = false;
        this.clearFiles();

        if (severity === Severity.Success) {
            this.dynamicPagesFacade.createNewEvent(
                SystemEntity.Import,
                DynamicPagesEventTypes.Reload,
            );
        }

        this.closeModal(severity === Severity.Success);
        this.toastService.show({
            body: [message],
            severity,
            closeOthers: true,
            bodyRouterLink: [...ROUTE_PREFIX, ...URL_DATA_MANAGEMENT_IMPORT],
        });
    }

    clearFiles(): void {
        this.files = [];
    }

    closeModal(closeParentModal = false): void {
        this.modalManagementService.closeActiveModal();

        if (closeParentModal) {
            this.modalManagementService.closeActiveModal();
        }

        this.clmiOnClosed.emit();
    }

    getProgressType(fileUploadStatus: ImportStatusType) {
        if (fileUploadStatus) {
            return {
                [ImportStatusType.Processing]: 'success',
                [ImportStatusType.Created]: 'success',
                [ImportStatusType.Completed]: 'success',
                [ImportStatusType.Errored]: 'danger',
            }[fileUploadStatus];
        }
        return null;
    }

    private getImportFormData(file: File): FormData {
        const formData = new FormData();
        formData.append(file.name, file);

        return formData;
    }
}
